import { CinImageLabellingMode } from "@cinnamon/design-system/compounds";
import { FileStatus } from "@cinnamon/design-system/types";

import { LIMIT_USAGES_STATUS } from "./limitUsagesStatus";

export enum SSOLoginType {
  SSO_LOGIN = "SSO_LOGIN",
  AUTH_CALLBACK = "AUTH_CALLBACK",
}

export enum LOGIN_METHOD {
  SSO = "sso",
  TRADITIONAL = "traditional",
}

export enum SSO_PROVIDER {
  AZURE_AD = "azure_ad",
}

export type QueryOperator = "$eq" | "$like" | "$in";

export type Sorting = "asc" | "desc";

export type SystemLanguage = "en" | "ja";
export type SystemLanguageCode = "EN" | "JP";

export enum LOCAL_STORAGE_NAME {
  TRUSTED_2FA_EMAIL_DEVICE_TOKEN = "trusted_2fa_email_device_token",
  SSO_LOGIN_INFO = "sso_login_info",
  BANNER_CLOSED = "banner_closed",
}

export interface LocalStorageType {
  access_token: string;
  language?: SystemLanguageCode;
}
export type FormTypeKey = "FREE_FORM" | "CUSTOMIZE";
export type ClassificationFormTypeKey = FormTypeKey | "MANUAL_CLASSIFICATION" | "AUTO_CLASSIFICATION";
export type DataExtractionFormTypeKey = FormTypeKey | "FIXED_FORM" | "LLM_IE";

export enum FORM_TYPE {
  FIXED_FORM = "fixed-form",
  LLM_IE = "llm-ie",
  FREE_FORM = "free-form",
  CUSTOMIZE = "vendor-customized",
  MANUAL_CLASSIFICATION = "manual-classification",
  AUTO_CLASSIFICATION = "auto-classification",
}

export const CLASSIFICATION_FORM_TYPE: Record<ClassificationFormTypeKey, Partial<FORM_TYPE>> = {
  FREE_FORM: FORM_TYPE.FREE_FORM,
  CUSTOMIZE: FORM_TYPE.CUSTOMIZE,
  MANUAL_CLASSIFICATION: FORM_TYPE.MANUAL_CLASSIFICATION,
  AUTO_CLASSIFICATION: FORM_TYPE.AUTO_CLASSIFICATION,
};

export const CLASSIFICATION_FORM_TYPE_OLD: Record<
  Exclude<ClassificationFormTypeKey, "AUTO_CLASSIFICATION">,
  Partial<FORM_TYPE>
> = {
  FREE_FORM: FORM_TYPE.FREE_FORM,
  CUSTOMIZE: FORM_TYPE.CUSTOMIZE,
  MANUAL_CLASSIFICATION: FORM_TYPE.MANUAL_CLASSIFICATION,
};

export const DATA_EXTRACTION_FORM_TYPE: Record<DataExtractionFormTypeKey, Partial<FORM_TYPE>> = {
  FIXED_FORM: FORM_TYPE.FIXED_FORM,
  LLM_IE: FORM_TYPE.LLM_IE,
  FREE_FORM: FORM_TYPE.FREE_FORM,
  CUSTOMIZE: FORM_TYPE.CUSTOMIZE,
};

export const DATE_FORMAT = {
  YYYY_MMM: "YYYY MMM",
  MMM_DD_YYYY: "MMM, DD YYYY",
  YYYY_MM_DD_DOT: "YYYY.MM.DD",
  YYYY_MM_DD_HH_MM_DOT: "YYYY.MM.DD hh:mm",
  YYYY_MM_DD_HH_MM_SS_DOT: "YYYY.MM.DD hh:mm:ss",
  YYYY_MM_DD_SLASH: "YYYY/MM/DD",
  YYYY_MM_DD_HH_MM_SLASH: "YYYY/MM/DD hh:mm",
  YYYY_MM_DD_HH_MM_SS_SLASH: "YYYY/MM/DD hh:mm:ss",
  DD_MM_YYYY_SLASH: "DD/MM/YYYY",
  YYYYMMDDhhmmss: "YYYYMMDDhhmmss",
};

export const WEEKDAY_SHORT = {
  1: "DATE.MONDAY_ALT",
  2: "DATE.TUESDAY_ALT",
  3: "DATE.WEDNESDAY_ALT",
  4: "DATE.THURSDAY_ALT",
  5: "DATE.FRIDAY_ALT",
  6: "DATE.SATURDAY_ALT",
  7: "DATE.SUNDAY_ALT",
};

export const MONTH_SHORT: Record<number, string> = {
  1: "DATE.JANUARY_ALT",
  2: "DATE.FEBRUARY_ALT",
  3: "DATE.MARCH_ALT",
  4: "DATE.APRIL_ALT",
  5: "DATE.MAY_ALT",
  6: "DATE.JUNE_ALT",
  7: "DATE.JULY_ALT",
  8: "DATE.AUGUST_ALT",
  9: "DATE.SEPTEMBER_ALT",
  10: "DATE.OCTOBER_ALT",
  11: "DATE.NOVEMBER_ALT",
  12: "DATE.DECEMBER_ALT",
};

export const USER_STATUS = {
  ACTIVE: "ACTIVE",
  DELETED: "DELETED",
};

export const HTTP_CODE = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
};

export const KEY_CODE = {
  ENTER: 13,
  SPACE: 32,
  // eslint-disable-next-line id-length
  Y: 89,
  // eslint-disable-next-line id-length
  Z: 90,
};

export const KEYBOARD_KEY = {
  ENTER: "Enter",
  ESCAPE: "Escape",
  // eslint-disable-next-line id-length
  S: "KeyS",
  SPACE: "Space",
  // eslint-disable-next-line id-length
  Y: "KeyY",
  // eslint-disable-next-line id-length
  Z: "KeyZ",
};

export const UPLOAD_STATUS: Record<Uppercase<string>, string> = {
  FAILED: "failed",
  PENDING: "pending",
  UPLOADING: "uploading",
  SUCCESS: "success",
};

export const UPLOAD_STATUS_TEXT = {
  [UPLOAD_STATUS.FAILED]: "COMMON.UPLOAD_STATUS_FAILED",
  [UPLOAD_STATUS.PENDING]: "COMMON.UPLOAD_STATUS_PENDING",
  [UPLOAD_STATUS.UPLOADING]: "COMMON.UPLOAD_STATUS_UPLOADING",
  [UPLOAD_STATUS.SUCCESS]: "COMMON.UPLOAD_STATUS_SUCCESS",
};

export const QUERY_OPERATOR: Record<string, QueryOperator> = {
  EQUAL: "$eq",
  LIKE: "$like",
  IN: "$in",
};

export type GlobalEvent = "logout";

export const GLOBAL_EVENTS: Record<Uppercase<GlobalEvent>, GlobalEvent> = {
  LOGOUT: "logout",
};

export const TABLE_CELL_TYPE = {
  ACTION: "action",
  CHECKBOX: "checkbox",
  INDEX: "index",
  STATUS: "status",
  TEXT: "text",
};

export const REFRESH_TOKEN_STORAGE_NAME = "refresh_token";

export const TOKEN_STORAGE_NAME = "token";

export const NO_ACCOUNT_FOUND_SERVER_ERROR = "No active account found with the given credentials";

export const INCORRECT_USER_LOGIN = "COMMON.INCORRECT_LOGIN_INFO";

export const ABLE_TO_DELETE_STATUSES = [
  FileStatus.PENDING,
  FileStatus.NEED_TO_REVIEW,
  FileStatus.DONE,
  FileStatus.ERROR,
];

export const ABLE_TO_DOWNLOAD_STATUSES = [FileStatus.NEED_TO_REVIEW, FileStatus.DONE];

export const ABLE_TO_RERUN_STATUSES = [FileStatus.ERROR];

export enum CLASSIFICATION_GROUP_BY {
  FILE = "file",
  PAGE = "page",
  DOCUMENT_ID = "document_id",
}

export const CLASSIFICATION_GROUP_BY_TEXT = {
  [CLASSIFICATION_GROUP_BY.FILE]: "COMMON.GROUP_BY_FILE",
  [CLASSIFICATION_GROUP_BY.PAGE]: "COMMON.GROUP_BY_PAGE",
  [CLASSIFICATION_GROUP_BY.DOCUMENT_ID]: "COMMON.GROUP_BY_DOCUMENT_ID",
};

export const FORM_TYPE_TEXT: Record<FORM_TYPE, string> = {
  [FORM_TYPE.FREE_FORM]: "COMMON.FORM_TYPE_GENERAL",
  [FORM_TYPE.FIXED_FORM]: "COMMON.FORM_TYPE_FIXED_FORM",
  [FORM_TYPE.LLM_IE]: "COMMON.FORM_TYPE_LLM_IE",
  [FORM_TYPE.CUSTOMIZE]: "COMMON.FORM_TYPE_CUSTOMIZED",
  [FORM_TYPE.MANUAL_CLASSIFICATION]: "COMMON.FORM_TYPE_MANUAL_CLASSIFICATION",
  [FORM_TYPE.AUTO_CLASSIFICATION]: "COMMON.FORM_TYPE_AUTO_CLASSIFICATION",
};

export const LABELLING_MODE_TEXT: Record<string, string> = {
  [CinImageLabellingMode.Edit]: "COMMON.EDIT_LINECUT",
  [CinImageLabellingMode.DrawRectangle]: "COMMON.CREATE_LINECUT",
  [CinImageLabellingMode.Select]: "COMMON.SELECT_LINECUT",
};

export const EDITABLE_FORM_TYPES = [
  FORM_TYPE.FIXED_FORM,
  FORM_TYPE.LLM_IE,
  FORM_TYPE.MANUAL_CLASSIFICATION,
  FORM_TYPE.AUTO_CLASSIFICATION,
];

export const MINIMUM_ALERT_CONFIDENCE_SCORE = 0.7;

export const LIMIT_USAGES_THEME_COLOR = {
  [LIMIT_USAGES_STATUS.NORMAL]: "var(--chakra-colors-blue-400)",
  [LIMIT_USAGES_STATUS.WARNING]: "var(--chakra-colors-orange-400)",
  [LIMIT_USAGES_STATUS.ERROR]: "var(--chakra-colors-red-400)",
};

export const LIMIT_USAGES_THEME_BG_COLOR = {
  [LIMIT_USAGES_STATUS.NORMAL]: "var(--chakra-colors-blue-50)",
  [LIMIT_USAGES_STATUS.WARNING]: "var(--chakra-colors-orange-50)",
  [LIMIT_USAGES_STATUS.ERROR]: "var(--chakra-colors-red-50)",
};

export enum SETTING_TYPE {
  GENERAL = "GENERAL",
  IP_RESTRICTION = "IP_RESTRICTION",
  TWO_FACTOR_AUTHENTICATION = "TWO_FACTOR_AUTHENTICATION",
  API_KEY_MANAGEMENT = "API_KEY_MANAGEMENT",
  LICENSE_MANAGEMENT = "LICENSE_MANAGEMENT",
  FILE_RETENTION_POLICY = "FILE_RETENTION_POLICY",
}

export enum WorkFlowType {
  OCR = "OCR",
  CLASSIFICATION = "CLASSIFICATION",
}

export const defaultCallback = (): void => {
  // Do nothing
};

export enum AI_PIPELINE_TYPE {
  DATA_EXTRACTION = "DATA_EXTRACTION",
  CLASSIFICATION = "CLASSIFICATION",
}

export enum FIX_FORM_CONFIG_TYPE {
  FIELDS = "FIX_FORM_FIELDS",
  TEMPLATES = "FIX_FORM_TEMPLATES",
  DATA_ENHANCEMENT = "FIX_FORM_DATA_ENHANCEMENT",
  RELEASES = "FIX_FORM_RELEASES",
}

export enum LLM_CONFIG_TYPE {
  AI_SETTING_MODEL = "LLM_AI_SETTING_MODEL",
  SINGLE_FIELDS = "LLM_SINGLE_FIELDS",
  TABLE_FIELDS = "LLM_TABLE_FIELDS",
  SAMPLES = "LLM_SAMPLES",
  DATA_ENHANCEMENT = "LLM_DATA_ENHANCEMENT",
  RELEASES = "LLM_RELEASES",
}

export enum MANUAL_CLASSIFICATION_CONFIG_TYPE {
  CLASS = "MANUAL_CLASSIFICATION_CLASS_CONFIG",
  RELEASES = "MANUAL_CLASSIFICATION_RELEASES",
}

export enum AUTO_CLASSIFICATION_CONFIG_TYPE {
  CLASS = "AUTO_CLASSIFICATION_CLASS_CONFIG",
  RELEASES = "AUTO_CLASSIFICATION_RELEASES",
}

export enum EXEC_TYPE {
  OCR = "ocr",
  CLASSIFICATION = "classification",
}

export enum BUILD_TYPE {
  PREBUILT = "prebuilt",
  VENDOR_CUSTOMIZED = "vendor-customized",
  CLIENT_CUSTOMIZED = "client-customized",
}

export enum HTTP_STATUS_CODE {
  OK = 200,
  CREATED = 201,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  NOT_FOUND = 404,
  CONFLICT = 409,
  INTERNAL_SERVER_ERROR = 500,
  SERVICE_UNAVAILABLE = 503,
}
